import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge, EmptyState, ErrorNote, Spinner } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { api, ApiError, type ApiKeySummary } from '@/lib/api'
import { formatDateTime, formatRelative } from '@/lib/format'
import { useAuth } from '@/features/auth/AuthProvider'
import { CreateKeyDialog } from './CreateKeyDialog'

/**
 * Credentials issued to consuming applications.
 *
 * Deliberately shows only the hint, never a key: the plaintext exists once, at
 * creation. A key that has been lost is revoked and reissued, not recovered.
 */
export function ApiKeysPage() {
  const { can } = useAuth()
  const [keys, setKeys] = useState<ApiKeySummary[]>([])
  const [buckets, setBuckets] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<ApiKeySummary | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    api
      .apiKeys()
      .then((res) => setKeys(res.keys))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load API keys'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    api
      .buckets()
      .then((res) => setBuckets(res.buckets.map((b) => b.name)))
      .catch(() => {
        // The create dialog simply offers nothing to pick if this fails.
      })
  }, [])

  async function revoke(key: ApiKeySummary) {
    try {
      await api.revokeApiKey(key.id)
      setRevoking(null)
      load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke the key')
    }
  }

  if (!can('admin')) {
    return (
      <EmptyState
        icon={<KeyRound className="h-8 w-8" />}
        title="Not available"
        description="Managing API keys requires the admin permission."
      />
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h1 className="text-lg font-semibold">API keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Credentials for applications that use this service. Each is scoped to specific
            buckets and permissions.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          New key
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div className="p-5">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Spinner className="h-5 w-5" />
          </div>
        )}

        {!loading && keys.length === 0 && !error && (
          <EmptyState
            icon={<KeyRound className="h-8 w-8" />}
            title="No API keys yet"
            description="Create one for each application that needs access."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New key
              </Button>
            }
          />
        )}

        {keys.length > 0 && (
          <table className="w-full min-w-[56rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-2.5 font-medium">Application</th>
                <th className="px-2 py-2.5 font-medium">Buckets</th>
                <th className="px-2 py-2.5 font-medium">Permissions</th>
                <th className="w-32 px-2 py-2.5 font-medium">Last used</th>
                <th className="w-28 px-2 py-2.5 font-medium">Status</th>
                <th className="w-16 px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="border-b border-border align-top">
                  <td className="px-5 py-3">
                    <p className="font-medium">{key.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{key.hint}</p>
                    <p className="text-xs text-muted-foreground">
                      by {key.createdBy} {formatRelative(key.createdAt)}
                      {key.expiresAt && ` · expires ${formatDateTime(key.expiresAt)}`}
                    </p>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex flex-wrap gap-1">
                      {key.buckets.map((b) => (
                        <Badge key={b} tone="neutral">
                          {b}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex flex-wrap gap-1">
                      {key.permissions.map((p) => (
                        <Badge
                          key={p}
                          // The destructive and outward-facing grants are worth
                          // spotting at a glance in a list of many keys.
                          tone={
                            p === 'admin'
                              ? 'danger'
                              : ['delete', 'delete-directory', 'publish', 'rename'].includes(p)
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-muted-foreground">
                    {key.lastUsedAt ? formatRelative(key.lastUsedAt) : 'never'}
                  </td>
                  <td className="px-2 py-3">
                    {key.active ? (
                      <Badge tone="success">Active</Badge>
                    ) : (
                      <Badge tone="danger">{key.disabled ? 'Revoked' : 'Expired'}</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {!key.disabled && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setRevoking(key)}
                        aria-label={`Revoke ${key.name}`}
                        title="Revoke"
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <CreateKeyDialog
          availableBuckets={buckets}
          onClose={() => setCreating(false)}
          onCreated={load}
        />
      )}

      {revoking && (
        <Dialog
          open
          onClose={() => setRevoking(null)}
          title={`Revoke "${revoking.name}"?`}
          description="This takes effect immediately and cannot be undone."
          footer={
            <>
              <Button onClick={() => setRevoking(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => void revoke(revoking)}>
                Revoke
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted-foreground">
            Any application still using this key will start receiving 401 responses on its next
            request. Issue a replacement before revoking if the app is in use.
          </p>
        </Dialog>
      )}
    </div>
  )
}
